import React, { useState, useEffect, useContext, useRef, useId } from "react";
import {
  Box,
  Dialog,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useParams } from "react-router-dom";

import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import NoData from "../../../components/NoData";
import { EstadoError } from "../../../components/base";
import { getAccountStatusAsync } from "../../../api/Report";
import { simpleMessage } from "../../../helpers/Helpers";
import { formatearMonto } from "../../../helpers/formato";
import { fechaDeCredito } from "../../credits/comunes";
import { ocultoVisual } from "../../clients/comunes";
import {
  EncabezadoReporte,
  HojaImpresion,
} from "../../../components/reportes/EncabezadoReporte";
import {
  diasMoraCuota,
  montoCuota,
  ordenarAbonos,
  ordenarCuotas,
  saldoDelPlan,
  totalAbonado,
} from "./estadoCuenta";

const TITULO = "Reporte de Estado de Cuenta";

// Lo que devuelve `errorResponse` cuando el servidor dice que la sesión sigue abierta en otro equipo.
const ERROR_OTRA_SESION = "Sesión activa en otro dispositivo";

// Mismo tamaño de letra y celdas angostas que los demás reportes (ReporteAgrupado).
const celdasAngostas = { "& .MuiTableCell-root": { px: 0.75 } };

// En papel todo va en negro.
const tinta = (impresion, color) => (impresion ? "#000" : color);

const formatearTasa = (tasa) =>
  `${new Intl.NumberFormat("es-NI", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(tasa)} %`;

const Dato = ({ etiqueta, children, ancho = 1, impresion }) => (
  <Box sx={{ minWidth: 0, gridColumn: `span ${ancho}` }}>
    <Typography
      component="dt"
      sx={{ fontSize: impresion ? 9 : 12, fontWeight: 700, color: tinta(impresion, "text.secondary") }}
    >
      {etiqueta}
    </Typography>
    <Typography
      component="dd"
      sx={{
        m: 0,
        fontSize: impresion ? 10 : 14,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: tinta(impresion, "text.primary"),
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </Typography>
  </Box>
);

const Encabezado = ({ children, derecha = false, impresion }) => (
  <TableCell
    align={derecha ? "right" : "center"}
    sx={{ fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", color: tinta(impresion, "primary.main") }}
  >
    {children}
  </TableCell>
);

const Celda = ({ children, derecha = false, envolver = false, impresion, ...props }) => (
  <TableCell
    align={derecha ? "right" : "center"}
    sx={{
      fontSize: 9,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: envolver ? "normal" : "nowrap",
      color: tinta(impresion, "text.primary"),
    }}
    {...props}
  >
    {children}
  </TableCell>
);

const TituloSeccion = ({ id, children, impresion }) => (
  <Typography
    id={id}
    component="h2"
    sx={{ fontSize: impresion ? 11 : 15, fontWeight: 700, color: tinta(impresion, "primary.main"), mb: 0.5 }}
  >
    {children}
  </Typography>
);

// En el teléfono la tabla se desplaza de lado: con foco, también se recorre con el teclado.
const ContenedorTabla = ({ impresion, etiquetaId, children }) => (
  <TableContainer
    {...(impresion ? {} : { tabIndex: 0, role: "region", "aria-labelledby": etiquetaId })}
  >
    {children}
  </TableContainer>
);

const Total = ({ etiqueta, valor, color, impresion }) => (
  <Stack sx={{ textAlign: "center" }}>
    <Typography sx={{ fontSize: impresion ? 10 : 14, color: tinta(impresion, "text.secondary") }}>
      {etiqueta}
    </Typography>
    <Typography
      sx={{
        fontSize: impresion ? 13 : 20,
        fontWeight: 700,
        fontVariantNumeric: "tabular-nums",
        color: tinta(impresion, color),
      }}
    >
      {formatearMonto(valor)}
    </Typography>
  </Stack>
);

/**
 * Pantalla y hoja impresa del estado de cuenta: salen del mismo dibujo, así que no pueden diferir.
 * `impresion` lo pasa a negro y con letra de papel.
 */
const CuerpoEstadoCuenta = ({ data, impresion }) => {
  const id = useId();
  const plan = ordenarCuotas(data.payments);
  const abonos = ordenarAbonos(data.quotes);
  const abonado = totalAbonado(data.quotes);
  // Como en el plan de pagos del crédito: la mora corre solo en un crédito entregado y vigente.
  const corriendo = data.isEntregado !== false && !data.isCanceled;
  const p = { impresion };

  return (
    <>
      <Typography component="h2" sx={ocultoVisual}>
        Datos del crédito
      </Typography>
      <Box
        component="dl"
        sx={{
          m: 0,
          textAlign: "left",
          display: "grid",
          gap: impresion ? 1 : 2,
          gridTemplateColumns: impresion
            ? "repeat(6, minmax(0, 1fr))"
            : {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
                lg: "repeat(6, minmax(0, 1fr))",
              },
        }}
      >
        <Dato {...p} etiqueta="Crédito">{`#${data.id}`}</Dato>
        <Dato {...p} etiqueta="Cliente" ancho={2}>
          {data.client?.fullName}
        </Dato>
        <Dato {...p} etiqueta="Monto entregado">{formatearMonto(data.montoEntregado)}</Dato>
        <Dato {...p} etiqueta="Monto a pagar">{formatearMonto(data.montoPagar)}</Dato>
        <Dato {...p} etiqueta="Cuota">{formatearMonto(data.cuotaPagar)}</Dato>
        <Dato {...p} etiqueta="Interés corriente">
          {data.tasaInteresCorriente ? formatearTasa(data.tasaInteresCorriente.intCorriente) : "—"}
        </Dato>
        <Dato {...p} etiqueta="Periodicidad">{data.periocidad?.description ?? "—"}</Dato>
        <Dato {...p} etiqueta="Plazo">{data.plazo?.description ?? "—"}</Dato>
        <Dato {...p} etiqueta="Fecha de entrega">{fechaDeCredito(data.fechaEntrega)}</Dato>
        <Dato {...p} etiqueta="Primera cuota">{fechaDeCredito(data.fechaPrimeraCuota)}</Dato>
        <Dato {...p} etiqueta="Vencimiento">{fechaDeCredito(data.fechaVencimiento)}</Dato>
        <Dato {...p} etiqueta="Cancelación">
          {data.isCanceled ? fechaDeCredito(data.fechaCancelacion) : "No cancelado"}
        </Dato>
      </Box>

      <Divider sx={{ my: impresion ? 1 : 2 }} />

      <Grid container spacing={impresion ? 1 : 3}>
        <Grid item xs={impresion ? 7 : 12} lg={7} component="section">
          <TituloSeccion {...p} id={`${id}-plan`}>
            Plan de pagos
          </TituloSeccion>
          <ContenedorTabla {...p} etiquetaId={`${id}-plan`}>
            <Table size="small" sx={celdasAngostas} aria-labelledby={`${id}-plan`}>
              <TableHead>
                <TableRow>
                  <Encabezado {...p}>#</Encabezado>
                  <Encabezado {...p}>F.Pago</Encabezado>
                  <Encabezado {...p}>Días</Encabezado>
                  <Encabezado {...p}>D.Mora</Encabezado>
                  <Encabezado {...p} derecha>
                    Cuota
                  </Encabezado>
                  <Encabezado {...p} derecha>
                    S.Principal
                  </Encabezado>
                  <Encabezado {...p} derecha>
                    S.Interés C.
                  </Encabezado>
                  <Encabezado {...p}>Cancelado</Encabezado>
                </TableRow>
              </TableHead>
              <TableBody>
                {plan.map((row) => (
                  <TableRow key={row.id}>
                    <Celda {...p} component="th" scope="row">
                      {row.numeroCuota}
                    </Celda>
                    <Celda {...p}>{fechaDeCredito(row.fechaPago)}</Celda>
                    <Celda {...p}>{row.periodicidad}</Celda>
                    <Celda {...p}>{row.isCanceled || corriendo ? diasMoraCuota(row) : 0}</Celda>
                    <Celda {...p} derecha>
                      {formatearMonto(montoCuota(row, data.isNominal))}
                    </Celda>
                    <Celda {...p} derecha>
                      {formatearMonto(row.saldoPrincipal)}
                    </Celda>
                    <Celda {...p} derecha>
                      {formatearMonto(row.saldoInteresCorriente)}
                    </Celda>
                    <Celda {...p}>{row.isCanceled ? "SÍ" : "NO"}</Celda>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ContenedorTabla>
        </Grid>

        <Grid item xs={impresion ? 5 : 12} lg={5} component="section">
          <TituloSeccion {...p} id={`${id}-abonos`}>
            Abonos
          </TituloSeccion>
          {abonos.length === 0 ? (
            <Typography sx={{ fontSize: impresion ? 9 : 14, color: tinta(impresion, "text.secondary"), py: 1 }}>
              Este crédito no tiene abonos.
            </Typography>
          ) : (
            <ContenedorTabla {...p} etiquetaId={`${id}-abonos`}>
              <Table size="small" sx={celdasAngostas} aria-labelledby={`${id}-abonos`}>
                <TableHead>
                  <TableRow>
                    <Encabezado {...p}>Transacción</Encabezado>
                    <Encabezado {...p}>Fecha Pago</Encabezado>
                    <Encabezado {...p}>Concepto</Encabezado>
                    <Encabezado {...p} derecha>
                      Total Recibido
                    </Encabezado>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {abonos.map((row) => (
                    <TableRow key={row.id}>
                      <Celda {...p} component="th" scope="row">
                        {row.id}
                      </Celda>
                      <Celda {...p}>{fechaDeCredito(row.fechaPago)}</Celda>
                      <Celda {...p} envolver>
                        {row.concepto}
                      </Celda>
                      <Celda {...p} derecha>
                        {formatearMonto(row.totalRecibido)}
                      </Celda>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="right"
                      sx={{ fontSize: 10, fontWeight: 700, color: tinta(impresion, "text.primary") }}
                    >
                      {`Total (${abonos.length} ${abonos.length === 1 ? "abono" : "abonos"}):`}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                        color: tinta(impresion, "primary.main"),
                      }}
                    >
                      {formatearMonto(abonado)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </ContenedorTabla>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: impresion ? 1 : 2 }} />

      <Stack
        component="section"
        aria-label="Saldo"
        direction={impresion ? "row" : { xs: "column", sm: "row" }}
        justifyContent="space-around"
        spacing={impresion ? 1 : 2}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Total {...p} etiqueta="Monto a pagar" valor={data.montoPagar} color="primary.main" />
        <Total {...p} etiqueta="Total abonado" valor={abonado} color="success.main" />
        <Total {...p} etiqueta="Saldo pendiente" valor={saldoDelPlan(data.payments)} color="warning.main" />
      </Stack>
    </>
  );
};

/**
 * Estado de cuenta de un crédito: sus datos, el plan de pagos, los abonos (sin los anulados)
 * y el saldo.
 */
const AccountStatus = () => {
  const [data, setData] = useState({});
  // null mientras carga; "ok", "error" o "conexion" al terminar.
  const [estado, setEstado] = useState(null);

  const compRef = useRef();
  const { selectedCredit } = useParams();
  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  const token = getToken();

  const cargar = async () => {
    setEstado(null);
    setIsLoading(true);
    let result;
    try {
      result = await getAccountStatusAsync(token, selectedCredit);
    } catch (e) {
      // Sin red, la llamada lanza en vez de devolver statusResponse false.
      setIsLoading(false);
      setEstado("conexion");
      return;
    }
    setIsLoading(false);
    if (!result.statusResponse) {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      if (result.error === ERROR_OTRA_SESION) simpleMessage(result.error, "error");
      setEstado("error");
      return;
    }
    setData(result.data.result || {});
    setEstado("ok");
  };

  useEffect(() => {
    cargar();
  }, []);

  const parametros = [{ label: "Crédito #", valor: selectedCredit }];

  let contenido = null;
  if (estado === "error" || estado === "conexion") {
    contenido = (
      <EstadoError
        titulo={
          estado === "conexion"
            ? "No se pudo conectar con el servidor"
            : "No se pudo cargar el estado de cuenta"
        }
        onReintentar={cargar}
      />
    );
  } else if (estado === "ok") {
    contenido = isEmpty(data) ? (
      <NoData titulo="Sin datos" descripcion={`No se encontró el crédito #${selectedCredit}.`} />
    ) : (
      <CuerpoEstadoCuenta data={data} impresion={false} />
    );
  }

  return (
    <div>
      <Dialog fullScreen open={true} PaperProps={{ "aria-label": TITULO }}>
        <EncabezadoReporte
          titulo={TITULO}
          parametros={parametros}
          impresionRef={compRef}
          margenMm={5}
        />

        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
          <Divider sx={{ my: 2 }} />
          {contenido}
        </Box>
      </Dialog>

      <div style={{ display: "none" }}>
        <HojaImpresion ref={compRef} titulo={TITULO} parametros={parametros}>
          {isEmpty(data) ? null : (
            <Box sx={{ mt: 1 }}>
              <CuerpoEstadoCuenta data={data} impresion />
            </Box>
          )}
        </HojaImpresion>
      </div>
    </div>
  );
};

export default AccountStatus;
